import type { NavBarConfig } from '@gm-commercial/navbar-model';
import type { Profile, Role } from '@gm-commercial/profile-model';
import type { Dispatch } from 'redux';

import {
  ADMIN_ROLES,
  FLEET_EXPERIENCE_ROLES,
} from '~/common/constants/common.constant';
import { AppNames } from '~/common/models/appNames.model';
import type { FeatureFlagValueType } from '~/common/models/featureFlags.model';

import type { NAV_DRAWER_MESSAGES } from '../GlobalNavDrawer.messages';
import { getAccountNavBarConfig } from './account.navDrawer.config';
import { getFleetNavBarConfig } from './fleet.navDrawer.config';
import {
  getLegacyNavDrawerConfig,
  LegacyPath,
} from './legacy.navDrawer.config';

export const getNavDrawerConfig = ({
  messages,
  profileParams,
  hasRole,
  isFleetMapFlagEnabled,
  appName,
  dispatch,
  sessionId,
  findFlagScope,
  canShowPlanManagementByEnablement = true,
  showTelematicsFleetNavSurfaces = true,
  hasEmerNotificationsAccess = false,
  isProtectOnly = false,
  isShopAddOnsEnabled = false,
  showGettingStarted = false,
  isHardwareEnabledInMarketplace = false,
  showCameraNavSurfaces = true,
  showFleetPrograms = true,
  showIntegrations = true,
  isNotificationAllowed = false,
}: {
  messages: typeof NAV_DRAWER_MESSAGES;
  profileParams: Profile['permissionsContextIds'];
  hasRole: (authorizedRoles: Role[]) => boolean;
  isFleetMapFlagEnabled: boolean;
  appName?: AppNames;
  dispatch?: Dispatch;
  sessionId?: string;
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
  /**
   * Shared BPS-related enablement (identified ∧ plan ∩ marketplace overlap). Gates fleet **Catalog**
   * and account **OnStar plans** nav items.
   */
  canShowPlanManagementByEnablement?: boolean;
  /**
   * Gates fleet map, safety, and insights, plus account telematics surfaces (notification Safety & Security, camera settings).
   * Derived in shell via `resolveShowTelematicsFleetNavSurfaces` (merged plan OST; fully entitled when `hasProduct(OST)`;
   * otherwise BPS marketplace must list OST for mixed-plan discovery).
   */
  showTelematicsFleetNavSurfaces?: boolean;
  /**
   * Shows notification Safety & Security for Protect accounts, for their emergency notifications,
   * and fleet Vehicle events History for Protect accounts without Telematics.
   */
  hasEmerNotificationsAccess?: boolean;
  /** EMER access with no Telematics vehicle, which links fleet Vehicle events straight to History. */
  isProtectOnly?: boolean;
  isShopAddOnsEnabled?: boolean;
  showGettingStarted?: boolean;
  /** When true, shows the Shop Add-Ons sub-item (DUALCAM or GTADAPTER present in marketplace). */
  isHardwareEnabledInMarketplace?: boolean;
  /**
   * Gates camera-dependent fleet nav surfaces (Video requests). Derived in shell from
   * `useCamerasSettingsAvailability`, where a failed camera-settings fetch means the org has no cameras.
   */
  showCameraNavSurfaces?: boolean;
  /** False while the organization record is loading, when there is no organization, when the lookup fails, and for non-US organizations. */
  showFleetPrograms?: boolean;
  /** False until the organization has an enrollment with at least one partner. */
  showIntegrations?: boolean;
  /**
   * Signed-in role can open notification settings. Defaults to hidden so a
   * caller that forgets the flag does not show Notifications to an Advisor.
   */
  isNotificationAllowed?: boolean;
}): NavBarConfig => {
  // If no appName is provided, return an empty config
  if (!appName) {
    return { items: [] };
  }
  const getDefaultNav = () => {
    if (hasRole(FLEET_EXPERIENCE_ROLES))
      return getFleetNavBarConfig({
        profileParams,
        isFleetMapFlagEnabled,
        findFlagScope,
        dispatch,
        sessionId,
        hasRole,
        canShowFleetCatalogByEnablement: canShowPlanManagementByEnablement,
        showTelematicsFleetNavSurfaces,
        hasEmerNotificationsAccess,
        isProtectOnly,
        showGettingStarted,
        isHardwareEnabledInMarketplace,
        showCameraNavSurfaces,
      });
    if (hasRole(ADMIN_ROLES))
      return getLegacyNavDrawerConfig({
        messages: messages.legacyMenu,
        profileParams,
        sessionId,
        dispatch,
        findFlagScope,
      });
    return { items: [] };
  };
  const getNavConfig = (appName: AppNames) => {
    if (isAccountAreaAppName(appName)) {
      return getAccountNavBarConfig({
        messages: messages.accountMenu,
        profileParams,
        findFlagScope,
        hasRole,
        showTelematicsFleetNavSurfaces,
        hasEmerNotificationsAccess,
        isShopAddOnsEnabled,
        showFleetPrograms,
        showIntegrations,
        isNotificationAllowed,
      });
    }
    return getDefaultNav();
  };
  return getNavConfig(appName);
};

/**
 * App sections that render the account nav config instead of the fleet/legacy nav config.
 * Pages outside of this set are considered part of "the fleet config" for nav-history tracking.
 */
export const ACCOUNT_AREA_APP_NAMES = [
  AppNames.ACCOUNT,
  AppNames.SETTINGS,
  AppNames.SUPPORT,
];

export const isAccountAreaAppName = (appName?: AppNames): boolean =>
  !!appName && ACCOUNT_AREA_APP_NAMES.includes(appName);

export const getAppNameFromPath = (path: string) => {
  const appName = Object.values(AppNames).find((appName) =>
    path.trim().startsWith(`/${appName}`)
  );
  // Ensures legacy views still show the old nav drawer items
  if (!appName) {
    if (
      Object.values(LegacyPath).find((legacyPath) =>
        path.trim().startsWith(`/${legacyPath}`)
      )
    ) {
      return AppNames.LEGACY;
    }
    //Ensures that pages outside the fleet but are still valid (e.g. unauthorized, missing permissions, etc.)
    //don't show a blank space for the navigation
    return AppNames.FLEET;
  }
  return appName;
};
